import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { prepareLogsDateRange } from 'infrastructure/functions/logs';
import { NoteLog } from 'infrastructure/classes/logs/note-log';

import useApi from '../use-api';

import type { DateRange } from 'infrastructure/types';
import type {
  IPaginationMeta,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';
import type { Dispatch, SetStateAction } from 'react';
import type { IPaginatedNoteLogs } from 'infrastructure/interfaces/i-note-log';

const noteLogApi = (patientGuid: string) =>
  `core-2/patients/${patientGuid}/deleted-note-logs`;

interface IUseNoteLogMeta {
  dateRange?: DateRange;
}

type mergedMeta = IUseNoteLogMeta & IPaginationMeta;

type UseLog = {
  loading: boolean;
  items: Array<NoteLog>;
  meta: mergedMeta;
  setMeta: Dispatch<SetStateAction<mergedMeta>>;
};

interface IUseLogProps {
  patientGuid: string;
  meta?: IUseNoteLogMeta & IPartialPaginationMeta;
}

export const useNoteLog = (props: IUseLogProps): UseLog => {
  const options = props;
  const { patientGuid, meta: metaProps = {} } = options;

  const [meta, setMeta] = useState<mergedMeta>({
    page: 1,
    items: 15,
    totalCount: 0,
    ...metaProps,
  });

  const { loadData } = useApi();

  const queryKey = [
    noteLogApi(patientGuid),
    prepareLogsDateRange(meta.dateRange),
    meta.page,
  ];

  const { data = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      loadData<IPaginatedNoteLogs>(noteLogApi(patientGuid), {
        page: meta.page,
        items: meta.items,
        ...prepareLogsDateRange(meta.dateRange),
      }).then((res) => {
        if (res) {
          const { count, data: rows } = res;

          setMeta({
            ...meta,
            totalCount: count,
          });

          return rows.map((el) => new NoteLog(el));
        }
      }),
    enabled: Boolean(patientGuid),
  });

  return {
    loading: isLoading,
    items: data,
    meta,
    setMeta,
  };
};
